/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.script.runtime

import io.peekandpoke.klang.common.SourceLocation
import io.peekandpoke.klang.script.KlangScriptLibrary
import io.peekandpoke.klang.script.builder.KlangScriptExtension
import io.peekandpoke.klang.script.utils.suggestNames
import kotlin.reflect.KClass

/**
 * Environment for storing variables and managing lexical scope
 *
 * The environment implements lexical scoping through a parent-chain pattern.
 * Each environment has:
 * - A map of variable names to their values
 * - A map tracking which variables are mutable (let) vs immutable (const)
 * - An optional parent environment
 *
 * Variable lookup traverses the scope chain from inner to outer scopes.
 *
 * Example scope chain:
 * ```
 * Global Environment (parent=null)
 *   └─> Function Environment (parent=Global)
 *       └─> Block Environment (parent=Function)
 * ```
 *
 * When looking up a variable:
 * 1. Check the current environment
 * 2. If not found, check the parent
 * 3. Continue up the chain until found or we reach the top
 *
 * Const protection:
 * Variables declared with `const` cannot be reassigned after initialization.
 * Attempting to reassign a const variable throws a RuntimeException.
 */
class Environment(
    /** Parent environment for scope chaining (null for global scope) */
    private val parent: Environment? = null,
) {
    /** Registry of all known libraries */
    private val libraries = mutableMapOf<String, KlangScriptLibrary>()

    /** Registry with all known native types */
    private val nativeTypes = mutableMapOf<KClass<*>, NativeTypeInfo>()

    /** Registry with all known native extension methods */
    private val nativeExtensionMethods = mutableMapOf<KClass<*>, MutableMap<String, NativeExtensionMethod>>()

    /** Registry with all known native extension properties */
    private val nativeExtensionProperties = mutableMapOf<KClass<*>, MutableMap<String, NativeExtensionProperty>>()

    /** Map of variable names to their runtime values in this scope */
    private val values = mutableMapOf<String, RuntimeValue>()

    /** Map tracking which variables are mutable (true for let, false for const) */
    private val mutable = mutableMapOf<String, Boolean>()

    /** Map of local names to exported names (for libraries with export aliasing) */
    private val exportAliases = mutableMapOf<String, String>()

    // caches
    private val getExtensionMethodCache = mutableMapOf<Pair<KClass<*>, String>, NativeExtensionMethod?>()
    private val getExtensionMethodNamesCache = mutableMapOf<KClass<*>, Set<String>>()
    private val getExtensionPropertyCache = mutableMapOf<Pair<KClass<*>, String>, NativeExtensionProperty?>()
    private val getAllRegisteredSupertypes = mutableMapOf<KClass<*>, List<KClass<*>>>()

    /**
     * Register a native extension (libraries, functions, types, objects, extension methods).
     *
     * Clears internal lookup caches and merges all registrations from the extension.
     *
     * @param native The extension containing registrations to apply
     */
    fun register(native: KlangScriptExtension) {
        // clear all caches
        getExtensionMethodCache.clear()
        getExtensionMethodNamesCache.clear()
        getExtensionPropertyCache.clear()
        getAllRegisteredSupertypes.clear()

        // Register all libraries
        libraries.putAll(native.libraries)

        // Register native functions
        nativeTypes.putAll(native.types)

        // Register all native extension methods and properties, merged PER NAME under each receiver.
        // A plain putAll replaced the receiver's whole map, so importing sprudel after stdlib silently
        // dropped every stdlib string method (found 2026-09-08, `LibraryExtensionMergeSpec`). A method
        // of the same name and receiver still belongs to the library imported last.
        native.extensionMethods.forEach { (receiver, methods) ->
            nativeExtensionMethods.getOrPut(receiver) { mutableMapOf() }.putAll(methods)
        }
        native.extensionProperties.forEach { (receiver, properties) ->
            nativeExtensionProperties.getOrPut(receiver) { mutableMapOf() }.putAll(properties)
        }

        // Define all native objects as values.
        //
        // Use wrapAsRuntimeValue so primitives (Double / Int / String / Boolean) are
        // exposed as their script-level types (NumberValue / StringValue / BooleanValue),
        // letting `@KlangScript.Constant val PI: Double` participate naturally in arithmetic
        // and comparison. Non-primitive values fall through to NativeObjectValue<T>, the
        // existing behavior for objects like SprudelPattern.
        native.objects.forEach { (name, obj) ->
            define(name, wrapAsRuntimeValue(obj))
        }

        // Define all native functions as values
        native.functions.forEach { entry ->
            define(entry.name, NativeFunctionValue(entry.name, entry.paramSpecs, entry.function))
        }
    }

    /**
     * Define a new variable in this environment
     *
     * If a variable with the same name already exists in this environment,
     * it will be overwritten. This does not affect parent scopes.
     *
     * @param name The variable name
     * @param value The runtime value to store
     * @param mutable Whether the variable can be reassigned (true for let, false for const)
     */
    fun define(name: String, value: RuntimeValue, mutable: Boolean = true) {
        values[name] = value
        this.mutable[name] = mutable
    }

    /**
     * Get a variable value, traversing the scope chain if needed
     *
     * Looks up the variable in this environment first. If not found,
     * recursively searches parent environments up the scope chain.
     *
     * @param name The variable name to look up
     * @return The runtime value associated with this variable
     * @throws RuntimeException if the variable is not found in any scope
     *
     * Example:
     * ```
     * val global = Environment()
     * global.define("x", NumberValue(10.0))
     *
     * val local = Environment(parent = global)
     * local.get("x")  // Returns NumberValue(10.0) from parent
     * ```
     */
    fun get(
        name: String,
        location: SourceLocation? = null,
        stackTrace: List<CallStackFrame> = emptyList(),
    ): RuntimeValue {
        return values[name] ?: parent?.get(name, location, stackTrace)
        ?: throw KlangScriptReferenceError(symbolName = name, location = location, callStackTrace = stackTrace)
    }

    /**
     * Assign (update) an existing variable in the scope chain
     *
     * Finds the variable in this environment or a parent scope and updates its value.
     * Throws AssignmentError if the variable is declared as const (immutable).
     * Throws ReferenceError if the variable doesn't exist in any scope.
     *
     * @param name The variable name to assign
     * @param value The new runtime value
     * @param location Optional source location for error reporting
     * @param stackTrace Optional call stack for error reporting
     * @throws KlangScriptAssignmentError if variable is const
     * @throws KlangScriptReferenceError if variable is not defined
     */
    fun assign(
        name: String,
        value: RuntimeValue,
        location: SourceLocation? = null,
        stackTrace: List<CallStackFrame> = emptyList(),
    ): RuntimeValue {
        return when {
            values.containsKey(name) -> {
                if (mutable[name] == false) {
                    throw KlangScriptAssignmentError(
                        variableName = name,
                        message = "Cannot reassign constant variable '$name'",
                        location = location,
                        callStackTrace = stackTrace,
                    )
                }
                values[name] = value
                value
            }

            parent != null -> parent.assign(name, value, location, stackTrace)
            else -> throw KlangScriptReferenceError(symbolName = name, location = location, callStackTrace = stackTrace)
        }
    }

    /**
     * Check if a variable exists in this environment or parent chain
     *
     * @param name The variable name to check
     * @return true if the variable exists anywhere in the scope chain
     */
    fun has(name: String): Boolean {
        return values.containsKey(name) || (parent?.has(name) == true)
    }

    /**
     * Mark symbols as exported with optional aliasing
     *
     * This is used by export statements to declare which symbols
     * should be accessible when importing this library, and under what names.
     *
     * @param exports List of (localName, exportedName) pairs
     *                localName is the variable name in the library
     *                exportedName is the name visible to importers
     */
    fun markExports(exports: List<Pair<String, String>>) {
        for ((localName, exportedName) in exports) {
            exportAliases[localName] = exportedName
        }
    }

    /**
     * Get all exported symbols from this environment
     *
     * This is used for importing library symbols. It returns only the
     * symbols that have been explicitly marked for export, using their
     * exported names (which may differ from local names due to aliasing).
     *
     * If no exports are marked, all symbols are considered exported
     * (backward compatibility for libraries without export statements).
     *
     * @return Map of exported names to their runtime values
     */
    fun getExportedSymbols(): Map<String, RuntimeValue> {
        return if (exportAliases.isEmpty()) {
            // No exports marked - export all symbols with their local names (backward compatibility)
            values.toMap()
        } else {
            // Export only explicitly marked symbols, using their exported names
            exportAliases.mapNotNull { (localName, exportedName) ->
                values[localName]?.let { value ->
                    exportedName to value
                }
            }.toMap()
        }
    }

    /**
     * Get all symbols defined in this environment (not including parent)
     *
     * This is used for importing library symbols. It returns a map of
     * all variables defined in this environment scope only.
     *
     * @return Map of variable names to their runtime values
     * @deprecated Use getExportedSymbols() for library imports
     */
    fun getAllSymbols(): Map<String, RuntimeValue> {
        return (parent?.getAllSymbols() ?: emptyMap()) + values.toMap()
    }

    /**
     * Load library source code by name (LibraryLoader interface implementation)
     *
     * This is called by the interpreter when executing import statements.
     * It applies any native registrations and returns the library source code.
     *
     * @param name The library name
     * @return The library source code
     * @throws RuntimeException if the library is not found
     */
    fun loadLibrary(name: String): String {
        val library = libraries[name] ?: if (parent != null) {
            return parent.loadLibrary(name)
        } else {
            // The house "did you mean" ranking (osaDistance, a threshold scaled to the name's length), as every other
            // not-found message has it.
            val available = getAllLibraryNames().sorted()
            val msg = "Library not found.${suggestNames(typed = name, available = available)}" +
                " Available libraries: ${available.joinToString(", ") { "'$it'" }}."

            throw KlangScriptImportError(libraryName = name, message = msg)
        }

        register(library.native)

        // Return source code (or empty string if library has no source)
        return library.sourceCode
    }

    /** Collect all registered library names across the environment chain. */
    private fun getAllLibraryNames(): Set<String> {
        val names = libraries.keys.toMutableSet()
        parent?.let { names.addAll(it.getAllLibraryNames()) }
        return names
    }

    /**
     * Get an extension method for a native type
     *
     * Used by the interpreter to lookup methods during member access evaluation.
     *
     * @param value The runtime value to get the extension method names for
     * @param methodName The method name
     * @return The extension method, or null if not found
     */
    fun getExtensionMethod(value: RuntimeValue, methodName: String): NativeExtensionMethod? {
        val obj = value.unpackForSupertypeLookup()
        val cls = obj::class

        return getExtensionMethodCache.getOrPut(cls to methodName) {
            val allTypes = getAllRegisteredSupertypes(obj)

            allTypes.firstNotNullOfOrNull { nativeExtensionMethods[it]?.get(methodName) }
                ?: parent?.getExtensionMethod(value, methodName)
        }
    }

    /**
     * A human-readable type name for error messages. A script value kind goes by its script name, the one the
     * editor's types use: `Number`, `String`, `Boolean`, `Array`, `Object`, `null`, `Function`.
     *
     * [NativeObjectValue.qualifiedName] is computed at wrap time from `kClass.simpleName`, which
     * is NULL for an anonymous or local class — sprudel builds patterns from 21 different
     * `object : SprudelPattern { ... }` expressions, so a user calling a misspelled method on one
     * of those got `Native type 'Unknown'`, which tells them nothing.
     *
     * The registered supertype is reachable from the anonymous class (that is how method
     * dispatch already works), so use ITS name instead: a sprudel pattern reports as
     * `SprudelPattern`. Falls back through the parent chain, then to "Unknown" only if nothing
     * in the hierarchy was ever registered.
     *
     * Caveat worth knowing before relying on this for anything finer than a message:
     * [getAllRegisteredSupertypes] filters `nativeTypes.keys` by `isInstance`, so the order is
     * REGISTRATION order, not hierarchy distance. With two registered types on one hierarchy you
     * get whichever was registered first, not the most specific.
     */
    fun getDisplayTypeName(value: RuntimeValue): String {
        // A script value kind goes by the name the script and the editor use (`Boolean`, not `BooleanValue`)
        scriptTypeName(value)?.let { return it }

        val obj = value.unpackForSupertypeLookup()

        obj::class.simpleName?.let { return it }

        // Anonymous/local class: getAllRegisteredSupertypes puts the concrete class first (whose
        // simpleName we already know is null), then the registered supertypes in REGISTRATION
        // order. So this picks the first REGISTERED supertype, which is good enough to name the
        // thing in an error message but is not necessarily the most specific one.
        getAllRegisteredSupertypes(obj).firstNotNullOfOrNull { it.simpleName }?.let { return it }

        return parent?.getDisplayTypeName(value) ?: "Unknown"
    }

    /**
     * Get all registered extension method names for a native type
     *
     * Used for error messages to suggest available methods.
     *
     * @param value The runtime value to get the extension method names for
     * @return List of method names
     */
    fun getExtensionMethodNames(value: RuntimeValue): Set<String> {
        val obj = value.unpackForSupertypeLookup()
        val cls = obj::class

        return getExtensionMethodNamesCache.getOrPut(cls) {
            val allTypes = getAllRegisteredSupertypes(obj)

            val local = allTypes.flatMap { nativeExtensionMethods[it]?.keys ?: emptyList() }.toSet()
            val parent = parent?.getExtensionMethodNames(value) ?: emptyList()

            local + parent
        }
    }

    /**
     * Get an extension property for a native type.
     *
     * Used by the interpreter to resolve no-parens member access (`receiver.foo`)
     * against registered property accessors. Returns null if no property by
     * [propertyName] is registered for [value]'s type or its supertypes.
     */
    fun getExtensionProperty(value: RuntimeValue, propertyName: String): NativeExtensionProperty? {
        val obj = value.unpackForSupertypeLookup()
        val cls = obj::class

        return getExtensionPropertyCache.getOrPut(cls to propertyName) {
            val allTypes = getAllRegisteredSupertypes(obj)

            allTypes.firstNotNullOfOrNull { nativeExtensionProperties[it]?.get(propertyName) }
                ?: parent?.getExtensionProperty(value, propertyName)
        }
    }

    /** The name the script and the editor's types use for a script value kind; null for a native object. */
    private fun scriptTypeName(value: RuntimeValue): String? = when (value) {
        is NumberValue -> "Number"
        is StringValue -> "String"
        is BooleanValue -> "Boolean"
        is ArrayValue -> "Array"
        is ObjectValue -> "Object"
        NullValue -> "null"
        is FunctionValue, is NativeFunctionValue, is BoundNativeMethod -> "Function"
        is NativeObjectValue<*> -> null
    }

    private fun RuntimeValue.unpackForSupertypeLookup() = when (this) {
        is NativeObjectValue<*> -> value
        else -> this
    }

    private fun getAllRegisteredSupertypes(obj: Any): List<KClass<*>> {
        val cls = obj::class

        return getAllRegisteredSupertypes.getOrPut(cls) {

            val superTypes = nativeTypes.keys.filter { type ->
                type.isInstance(obj)
//                    .also {
//                        println("Checking type.isInstance(obj) | $type -> $it")
//                    }
            }

//            println("Super types of $cls: $superTypes")

            listOf(cls) + superTypes
        }
    }
}
